/**
 * batch-70 app-dashboard (FO-526, spec 226) - Systeem > App dashboard: per FO/spec
 * wat er in de app is aangepast, waar, wanneer het live ging en welke actiepunten
 * (FO-514) er nog open staan.
 *
 * De gedeelde bron voor api, backoffice en het sync-/backfillscript: de enums met
 * labels, het CONTRACT van de deployregistratie (POST /systeem/deploys) en van het
 * bronnenpakket (sync en backfill), de lijstfilters, de Zod-schema's van de
 * mutaties (de api valideert ermee, het formulier vooraf) en de DTO's. Puur: geen
 * DB, geen IO.
 *
 * Nooit stil schatten: een geschatte livegang en een geschatte plek in de app
 * dragen hun vlag (`liveOpGeschat`, `waarGeschat`) tot in de UI en de export.
 */
import { z } from 'zod'
import type { ActiepuntRijDTO } from './actiepunten'
import { queryBoolean } from './zod-query'

// ─── Enums (spiegel van de Prisma-enums) ────────────────────────────────────

export const APP_WIJZIGING_STATUSSEN = ['ONTWERP', 'IN_BOUW', 'GEBOUWD', 'LIVE', 'TERUGGEDRAAID'] as const
export type AppWijzigingStatus = (typeof APP_WIJZIGING_STATUSSEN)[number]

export const APP_WIJZIGING_STATUS_LABELS: Record<AppWijzigingStatus, string> = {
  ONTWERP: 'Ontwerp',
  IN_BOUW: 'In bouw',
  GEBOUWD: 'Gebouwd, nog niet live',
  LIVE: 'Live',
  TERUGGEDRAAID: 'Teruggedraaid',
}

/** De tab "Nog niet live" (spec 226 §3.A4). */
export const APP_NIET_LIVE_STATUSSEN = ['ONTWERP', 'IN_BOUW', 'GEBOUWD'] as const satisfies readonly AppWijzigingStatus[]

export const APP_DEPLOY_BRONNEN = ['CI', 'HANDMATIG', 'BACKFILL', 'SYNC'] as const
export type AppDeployBron = (typeof APP_DEPLOY_BRONNEN)[number]

export const APP_DEPLOY_BRON_LABELS: Record<AppDeployBron, string> = {
  CI: 'Deploy-workflow',
  HANDMATIG: 'Handmatig geregistreerd',
  BACKFILL: 'Terugwerkende import',
  SYNC: 'Sync uit de git-historie',
}

export const APP_LIVE_OP_BRONNEN = ['DEPLOY_REGISTRATIE', 'GITHUB_RUN', 'BUMP_COMMIT', 'SECTIEDATUM', 'HANDMATIG'] as const
export type AppLiveOpBron = (typeof APP_LIVE_OP_BRONNEN)[number]

export const APP_LIVE_OP_BRON_LABELS: Record<AppLiveOpBron, string> = {
  DEPLOY_REGISTRATIE: 'Geregistreerde deploy (workflow of beheer)',
  GITHUB_RUN: 'Einde van de deploy-run (GitHub)',
  BUMP_COMMIT: 'Geschat uit de versie-commit van de deploy',
  SECTIEDATUM: 'Geschat uit de datum van de CHANGELOG-sectie',
  HANDMATIG: 'Handmatig ingevuld',
}

/** De bronnen waarvan het moment een schatting is (altijd zichtbaar als "≈"). */
export const APP_LIVE_OP_GESCHATTE_BRONNEN = ['BUMP_COMMIT', 'SECTIEDATUM'] as const satisfies readonly AppLiveOpBron[]

export function isLiveOpGeschat(bron: AppLiveOpBron): boolean {
  return (APP_LIVE_OP_GESCHATTE_BRONNEN as readonly string[]).includes(bron)
}

export const APP_WAAR_BRONNEN = ['WAAR_REGEL', 'GEEN_SCHERM', 'MODULE_STANDAARD', 'HANDMATIG', 'ONBEKEND'] as const
export type AppWaarBron = (typeof APP_WAAR_BRONNEN)[number]

export const APP_WAAR_BRON_LABELS: Record<AppWaarBron, string> = {
  WAAR_REGEL: 'Uit de CHANGELOG (Waar:)',
  GEEN_SCHERM: 'Geen scherm (achtergrond)',
  MODULE_STANDAARD: 'Geschat uit de module',
  HANDMATIG: 'Handmatig ingevuld',
  ONBEKEND: 'Waar onbekend',
}

export const APP_WIJZIGING_HERKOMSTEN = ['CHANGELOG', 'SPEC', 'HANDMATIG'] as const
export type AppWijzigingHerkomst = (typeof APP_WIJZIGING_HERKOMSTEN)[number]

export const APP_WIJZIGING_AUDIT_ACTIES = [
  'AANGEMAAKT',
  'BIJGEWERKT_UIT_BRON',
  'STATUS_GEWIJZIGD',
  'LIVE_GEZET',
  'BEWERKT',
  'STATUS_VASTGEZET',
  'STATUS_VRIJGEGEVEN',
  'SAMENGEVOEGD',
  'LOSGEMAAKT',
  'NIET_MEER_IN_BRON',
  'WEER_IN_BRON',
  'VERWIJDERD',
] as const
export type AppWijzigingAuditActie = (typeof APP_WIJZIGING_AUDIT_ACTIES)[number]

export const APP_WIJZIGING_AUDIT_ACTIE_LABELS: Record<AppWijzigingAuditActie, string> = {
  AANGEMAAKT: 'Aangemaakt',
  BIJGEWERKT_UIT_BRON: 'Bijgewerkt uit de bron',
  STATUS_GEWIJZIGD: 'Status gewijzigd',
  LIVE_GEZET: 'Live gegaan',
  BEWERKT: 'Bewerkt',
  STATUS_VASTGEZET: 'Status vastgezet',
  STATUS_VRIJGEGEVEN: 'Status weer automatisch',
  SAMENGEVOEGD: 'Samengevoegd',
  LOSGEMAAKT: 'Losgemaakt',
  NIET_MEER_IN_BRON: 'Niet meer in de bron',
  WEER_IN_BRON: 'Weer in de bron',
  VERWIJDERD: 'Verwijderd',
}

/** De vier apps van de monorepo (een deploy zet ze allemaal live). */
export const APP_NAMEN = ['api', 'portal', 'backoffice', 'klant'] as const
export type AppNaam = (typeof APP_NAMEN)[number]

export const APP_NAAM_LABELS: Record<AppNaam, string> = {
  api: 'API',
  portal: 'Portal',
  backoffice: 'Backoffice',
  klant: 'Klantenportaal',
}

// ─── "Waar in de app" ───────────────────────────────────────────────────────

/** De apps met schermen; een pad zonder voorvoegsel hoort bij de backoffice. */
export const APP_WAAR_APPS = ['backoffice', 'portal', 'klant'] as const
export type AppWaarApp = (typeof APP_WAAR_APPS)[number]

/**
 * Eén plek in de app: `/voorraad/besteladvies` (backoffice), `portal:/verlof` of
 * `klant:/werkbonnen`. Altijd een relatief pad: nooit een extern adres.
 */
export const APP_WAAR_ITEM_RE = /^(?:(portal|klant):)?\/(?!\/)[A-Za-z0-9\-._~/%[\]]*$/

export const appWaarItemSchema = z
  .string()
  .trim()
  .max(200, { message: 'Maximaal 200 tekens per plek' })
  .regex(APP_WAAR_ITEM_RE, {
    message: 'Gebruik een pad dat met / begint, met portal: of klant: ervoor voor die apps (bv. portal:/verlof)',
  })

export function waarApp(item: string): AppWaarApp {
  if (item.startsWith('portal:')) return 'portal'
  if (item.startsWith('klant:')) return 'klant'
  return 'backoffice'
}

export function waarPad(item: string): string {
  return item.replace(/^(portal|klant):/, '')
}

/** De apps van een lijst plekken (uniek, in de volgorde van APP_WAAR_APPS). */
export function waarApps(waar: readonly string[]): AppWaarApp[] {
  const apps = new Set(waar.map(waarApp))
  return APP_WAAR_APPS.filter((a) => apps.has(a))
}

// ─── Lijst (query) ──────────────────────────────────────────────────────────

/** Tabs: live (gegroepeerd per deploy), nog niet live, of alles. */
export const APP_WIJZIGING_WEERGAVEN = ['live', 'nietLive', 'alle'] as const
export type AppWijzigingWeergave = (typeof APP_WIJZIGING_WEERGAVEN)[number]

const ymdSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Gebruik een datum als jjjj-mm-dd' })
  .refine((v) => {
    const d = new Date(`${v}T00:00:00.000Z`)
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v
  }, { message: 'Dit is geen bestaande datum' })

export const appWijzigingenLijstQuerySchema = z.object({
  weergave: z.enum(APP_WIJZIGING_WEERGAVEN).default('live'),
  status: z.enum(APP_WIJZIGING_STATUSSEN).optional(),
  module: z.string().trim().max(80).optional(),
  app: z.enum(APP_WAAR_APPS).optional(),
  /** Live vanaf (NL-kalenderdag). */
  van: ymdSchema.optional(),
  /** Live tot en met (NL-kalenderdag). */
  tot: ymdSchema.optional(),
  deployId: z.string().trim().min(1).max(64).optional(),
  metOpenActies: queryBoolean().optional(),
  alleenBeslispunten: queryBoolean().optional(),
  zonderFo: queryBoolean().optional(),
  waarOnbekend: queryBoolean().optional(),
  geschat: queryBoolean().optional(),
  nietGeparseerd: queryBoolean().optional(),
  /** FO-nummer (ook een oud nummer), specnummer, titel of sleutel. */
  zoek: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type AppWijzigingenLijstQuery = z.infer<typeof appWijzigingenLijstQuerySchema>

/** De filters die in de URL staan (en ook de export-body vormen). */
export const APP_WIJZIGING_FILTER_SLEUTELS = [
  'weergave',
  'status',
  'module',
  'app',
  'van',
  'tot',
  'deployId',
  'metOpenActies',
  'alleenBeslispunten',
  'zonderFo',
  'waarOnbekend',
  'geschat',
  'nietGeparseerd',
  'zoek',
] as const
export type AppWijzigingFilterSleutel = (typeof APP_WIJZIGING_FILTER_SLEUTELS)[number]

/**
 * "FO-512" -> FO 512, "spec 212" -> spec 212, "512" -> allebei (voor zoeken op een
 * (oud) nummer). Een FO-nummer is geen specnummer: "FO-110" vindt spec 110 niet.
 */
export function nummerUitZoekterm(zoek: string): { nummer: number; soort: 'FO' | 'SPEC' | 'BEIDE' } | null {
  const m = /^\s*(fo|spec)?[\s-]*0*(\d{1,4})\s*$/i.exec(zoek)
  if (!m) return null
  const voor = (m[1] ?? '').toLowerCase()
  return { nummer: Number(m[2]), soort: voor === 'fo' ? 'FO' : voor === 'spec' ? 'SPEC' : 'BEIDE' }
}

/** "FO-009", "FO-512": een FO-nummer zoals de bronnen het schrijven. */
export function foLabel(nummer: number | null | undefined): string | null {
  if (nummer === null || nummer === undefined) return null
  return `FO-${String(nummer).padStart(3, '0')}`
}

// ─── Mutaties (één schema: api + formulier) ─────────────────────────────────

const optioneleTekst = (max: number, melding: string) => z.string().trim().max(max, { message: melding })

export const appWijzigingBewerkenSchema = z
  .object({
    titel: z.string().trim().min(3, { message: 'Minimaal 3 tekens' }).max(200, { message: 'Maximaal 200 tekens' }).optional(),
    module: optioneleTekst(80, 'Maximaal 80 tekens').nullable().optional(),
    wat: optioneleTekst(20_000, 'Maximaal 20.000 tekens').nullable().optional(),
    /** Een eigen lijst plekken (wordt HANDMATIG). */
    waar: z.array(appWaarItemSchema).max(20, { message: 'Maximaal 20 plekken' }).optional(),
    /** De plekken weer uit de bron laten komen (CHANGELOG of module). */
    waarAutomatisch: z.literal(true).optional(),
    /** De status vastzetten (de sync verandert hem dan niet meer). */
    status: z.enum(APP_WIJZIGING_STATUSSEN).optional(),
    /** De status weer laten afleiden uit de bronnen. */
    statusAutomatisch: z.literal(true).optional(),
    /** Null = geen reden (zoals de read-DTO hem teruggeeft); verplicht bij TERUGGEDRAAID. */
    statusReden: optioneleTekst(2000, 'Maximaal 2000 tekens').nullable().optional(),
    /** Handmatig live-moment (ISO, met tijdzone); null wist een handmatig moment. */
    liveOp: z.string().datetime({ offset: true, message: 'Gebruik een datum en tijd' }).nullable().optional(),
    notities: optioneleTekst(5000, 'Maximaal 5000 tekens').nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.status === 'TERUGGEDRAAID' && !v.statusReden) {
      ctx.addIssue({ code: 'custom', path: ['statusReden'], message: 'Geef aan waarom de wijziging is teruggedraaid' })
    }
    if (v.status && v.statusAutomatisch) {
      ctx.addIssue({ code: 'custom', path: ['status'], message: 'Kies een status of laat hem automatisch bepalen, niet allebei' })
    }
    if (v.waar && v.waarAutomatisch) {
      ctx.addIssue({ code: 'custom', path: ['waar'], message: 'Vul de plekken in of laat ze uit de bron komen, niet allebei' })
    }
  })
  .refine((v) => Object.values(v).some((w) => w !== undefined), { message: 'Er is niets gewijzigd' })
export type AppWijzigingBewerkenInput = z.infer<typeof appWijzigingBewerkenSchema>

/** Handmatig een wijziging toevoegen (Azure-instelling, datafix): ADMIN. */
export const appWijzigingAanmakenSchema = z.object({
  titel: z.string().trim().min(3, { message: 'Minimaal 3 tekens' }).max(200, { message: 'Maximaal 200 tekens' }),
  module: optioneleTekst(80, 'Maximaal 80 tekens').optional(),
  wat: optioneleTekst(20_000, 'Maximaal 20.000 tekens').optional(),
  waar: z.array(appWaarItemSchema).max(20, { message: 'Maximaal 20 plekken' }).optional(),
  status: z.enum(APP_WIJZIGING_STATUSSEN).default('LIVE'),
  liveOp: z.string().datetime({ offset: true, message: 'Gebruik een datum en tijd' }).optional(),
  foNummer: z.coerce.number().int().min(1).max(9999).optional(),
  notities: optioneleTekst(5000, 'Maximaal 5000 tekens').optional(),
})
export type AppWijzigingAanmakenInput = z.infer<typeof appWijzigingAanmakenSchema>

/** De rij `aliasId` gaat op in de rij van de route (`:id` blijft). */
export const appWijzigingSamenvoegenSchema = z.object({
  aliasId: z.string().trim().min(1, { message: 'Kies de rij die je wilt samenvoegen' }).max(64),
  reden: optioneleTekst(500, 'Maximaal 500 tekens').optional(),
})
export type AppWijzigingSamenvoegenInput = z.infer<typeof appWijzigingSamenvoegenSchema>

// ─── Contract: deployregistratie en bronnenpakket ───────────────────────────

/** Een commit-SHA (7-40 hex), in kleine letters. */
export const appShaSchema = z
  .string()
  .trim()
  .regex(/^[0-9a-fA-F]{7,40}$/, { message: 'Een commit-SHA bestaat uit 7 tot 40 hexadecimale tekens' })
  .transform((v) => v.toLowerCase())

const isoMoment = z.string().datetime({ offset: true, message: 'Gebruik een ISO-moment met tijdzone (UTC: ...Z)' })

export const appVersiesSchema = z
  .object({
    api: z.string().trim().max(40).optional(),
    portal: z.string().trim().max(40).optional(),
    backoffice: z.string().trim().max(40).optional(),
    klant: z.string().trim().max(40).optional(),
  })
  .strict()
export type AppVersies = z.infer<typeof appVersiesSchema>

/** Eén specbestand zoals het script het aanlevert: de naam, de kop (begin van het bestand) en git-data. */
export const appSpecBronSchema = z.object({
  /** `226-systeem-app-dashboard-....md` (alleen de bestandsnaam). */
  bestand: z.string().trim().regex(/^\d{1,4}-[A-Za-z0-9._-]+\.md$/, { message: 'Een genummerd specbestand (NNN-naam.md)' }).max(200),
  /** Het begin van het bestand (kop, metadata, eerste sectie). */
  kop: z.string().max(40_000),
  /** Eerste commit op main die het bestand toevoegde. */
  gemaaktOp: isoMoment.optional(),
  gewijzigdOp: isoMoment.optional(),
  /** Eerdere namen (git-rename), voor de hernummerherkenning. */
  oudePaden: z.array(z.string().max(300)).max(20).optional(),
})
export type AppSpecBron = z.infer<typeof appSpecBronSchema>

/** Een spec die in een lopende batch wordt gebouwd (kickoff zonder STATUS-OCHTEND). */
export const appInBouwSchema = z.object({
  spec: z.number().int().min(1).max(9999),
  batch: z.number().int().min(1).max(999),
})

/** De bronnen bij een deploy of een sync: de CHANGELOG en de specs op die commit. */
export const appBronnenSchema = z.object({
  changelog: z.string().max(16_000_000),
  specs: z.array(appSpecBronSchema).max(2000),
  inBouw: z.array(appInBouwSchema).max(500).optional(),
})
export type AppBronnen = z.infer<typeof appBronnenSchema>

/**
 * Een link naar een run van GitHub Actions. Alleen https://github.com/: het adres komt
 * van de CI-stap of uit een sync-pakket en wordt in de backoffice een klikbare link
 * (een `javascript:`-adres zou daar code uitvoeren).
 */
export const appGithubRunUrlSchema = z
  .string()
  .trim()
  .max(300)
  .url({ message: 'Een volledige link' })
  .refine((u) => u.startsWith('https://github.com/'), { message: 'Een link naar github.com' })

/**
 * POST /systeem/deploys - de registratie van één deploy (CI-stap of handmatig).
 * Idempotent: dezelfde `workflowRunId` (of zonder run: dezelfde sha en liveOp) is
 * dezelfde registratie. Dezelfde sha met een andere run is een herdeploy: een
 * eigen rij met `herdeployVanId`, de wijzigingen worden niet gedupliceerd. Met
 * `bronnen` gaat elke CHANGELOG-sectie die nog niet live stond live met deze deploy.
 */
export const appDeployRegistratieSchema = z.object({
  sha: appShaSchema,
  /** Het moment dat de deploy-job slaagde (UTC). */
  liveOp: isoMoment,
  gestartOp: isoMoment.optional(),
  imageTag: z.string().trim().max(128).optional(),
  versies: appVersiesSchema.optional(),
  apps: z.array(z.enum(APP_NAMEN)).min(1).max(4).optional(),
  workflowRunId: z.string().trim().regex(/^\d{1,20}$/, { message: 'Het run-id van GitHub Actions (cijfers)' }).optional(),
  workflowRunUrl: appGithubRunUrlSchema.optional(),
  bronnen: appBronnenSchema.optional(),
  /**
   * Met de hand geregistreerd (beheer, het scherm): telt nooit als registratie door de
   * deploy-workflow en overschrijft geen exact moment, ook niet met een run-link erbij.
   */
  handmatig: z.boolean().optional(),
})
export type AppDeployRegistratieInput = z.infer<typeof appDeployRegistratieSchema>

/** Een deploy uit de git-historie (bump-commit), voor sync en backfill. */
export const appDeployBronSchema = z.object({
  registratieSleutel: z.string().trim().min(3).max(200),
  sha: appShaSchema,
  liveOp: isoMoment,
  liveOpBron: z.enum(['GITHUB_RUN', 'BUMP_COMMIT']),
  gestartOp: isoMoment.optional(),
  versies: appVersiesSchema.optional(),
  apps: z.array(z.enum(APP_NAMEN)).max(4).optional(),
  workflowRunId: z.string().trim().regex(/^\d{1,20}$/).optional(),
  workflowRunUrl: appGithubRunUrlSchema.optional(),
  bumpCommit: appShaSchema.optional(),
})
export type AppDeployBronInput = z.infer<typeof appDeployBronSchema>

/** Per CHANGELOG-sectie (sleutel): wanneer hij op main landde en met welke deploy hij live ging. */
export const appSectieHistorieSchema = z.record(
  z.string().max(80),
  z.object({
    landingOp: isoMoment.nullable(),
    deploySleutel: z.string().max(200).nullable(),
    /** De sleutels van deze kop voordat hij in dezelfde regel werd bijgewerkt (nieuwste eerst). */
    vorigeSleutels: z.array(z.string().max(80)).max(20).optional(),
  }),
)

/**
 * POST /systeem/app-dashboard/sync en POST /systeem/deploys/backfill - het
 * bronnenpakket van het script `app-dashboard-bronnen.ts`: de CHANGELOG en de
 * specs op `refSha` (main), de deploys uit de bump-commits en per sectie wanneer
 * hij landde en live ging. Idempotent: twee keer hetzelfde pakket verandert niets.
 */
export const appBronnenPakketSchema = appBronnenSchema.extend({
  ref: z.string().trim().min(1).max(100),
  refSha: appShaSchema,
  gegenereerdOp: isoMoment,
  deploys: z.array(appDeployBronSchema).max(5000).optional(),
  sectieHistorie: appSectieHistorieSchema.optional(),
})
export type AppBronnenPakket = z.infer<typeof appBronnenPakketSchema>

// ─── DTO's ──────────────────────────────────────────────────────────────────

export interface AppDeployDTO {
  id: string
  sha: string
  shaKort: string
  imageTag: string | null
  versies: AppVersies
  apps: string[]
  workflowRunId: string | null
  workflowRunUrl: string | null
  gestartOp: string | null
  liveOp: string
  liveOpBron: AppLiveOpBron
  liveOpGeschat: boolean
  bron: AppDeployBron
  batch: number | null
  herdeployVanId: string | null
  /** Hoeveel wijzigingen met deze deploy live gingen (over alles, niet de pagina). */
  aantalWijzigingen: number
}

export interface AppWijzigingSectieDTO {
  sleutel: string
  kop: string
  /** 'yyyy-MM-dd' of null ([Unreleased], een kop zonder datum). */
  datum: string | null
  titel: string
  module: string | null
  tekst: string
  waar: string[]
  /** De sectie zegt `Waar: geen` (geen scherm). */
  waarGeen: boolean
  heeftWaarRegel: boolean
  geparseerd: boolean
  parseFout: string | null
  batch: number | null
  /** Moment dat de sectie op main landde (ISO), of null. */
  landingOp: string | null
}

export interface AppActiesSamenvattingDTO {
  /** Open actiepunten (OPEN, BEZIG, GEBLOKKEERD). */
  open: number
  /** Waarvan met prioriteit "blokkeert een functie". */
  blokkerend: number
  /** Open beslispunten zonder besluit. */
  beslispunten: number
  /** Alle gekoppelde punten (ook afgerond). */
  totaal: number
}

export interface AppWijzigingRijDTO {
  id: string
  sleutel: string
  foNummer: number | null
  /** "FO-512" of null (een sectie of spec zonder FO). */
  foLabel: string | null
  foAliassen: number[]
  specNummer: number | null
  specAliassen: number[]
  titel: string
  module: string | null
  /** Het begin van "wat is er aangepast" (de rest in het detail). */
  watKort: string | null
  waar: string[]
  waarBron: AppWaarBron
  waarGeschat: boolean
  status: AppWijzigingStatus
  statusVastgezet: boolean
  liveOp: string | null
  liveOpGeschat: boolean
  liveOpBron: AppLiveOpBron | null
  gebouwdOp: string | null
  specGemaaktOp: string | null
  batch: number | null
  deploy: AppDeployDTO | null
  acties: AppActiesSamenvattingDTO
  geparseerd: boolean
  herkomst: AppWijzigingHerkomst
  /** De bronnen leveren deze rij niet meer (sectie of spec verdwenen). */
  nietMeerInBron: boolean
  /** Hoeveel rijen in deze rij zijn opgegaan (samengevoegd). */
  aantalSamengevoegd: number
}

export interface AppTijdlijnStapDTO {
  soort: 'SPEC' | 'GEBOUWD' | 'LIVE' | 'ACTIES_AFGEROND' | 'TERUGGEDRAAID'
  label: string
  /** ISO-moment, of null als de stap (nog) niet gezet is. */
  op: string | null
  geschat: boolean
  gedaan: boolean
  toelichting: string | null
}

export interface AppWijzigingAuditDTO {
  id: string
  actie: AppWijzigingAuditActie
  oudStatus: AppWijzigingStatus | null
  nieuwStatus: AppWijzigingStatus | null
  automatisch: boolean
  actor: { id: string; naam: string } | null
  detail: string | null
  op: string
}

export interface AppWijzigingKortDTO {
  id: string
  sleutel: string
  titel: string
  foLabel: string | null
}

export interface AppWijzigingDetailDTO extends AppWijzigingRijDTO {
  samenvatting: string | null
  wat: string | null
  secties: AppWijzigingSectieDTO[]
  specBestand: string | null
  specBestanden: string[]
  specStatusTekst: string | null
  foBron: string | null
  statusReden: string | null
  teruggedraaidOp: string | null
  notities: string | null
  parseFout: string | null
  aliasVan: AppWijzigingKortDTO | null
  samengevoegd: AppWijzigingKortDTO[]
  /** De gekoppelde actiepunten uit het register van FO-514 (via bronFo/bronBatch). */
  actiepunten: ActiepuntRijDTO[]
  tijdlijn: AppTijdlijnStapDTO[]
  audit: AppWijzigingAuditDTO[]
  auditTotaal: number
  /** Mag de kijker bewerken, samenvoegen en losmaken (ADMIN). */
  magBeheren: boolean
  /** Mag de kijker het register van FO-514 openen (link naar het punt). */
  magActiepuntenZien: boolean
}

export interface AppWijzigingenLijstMetaDTO {
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface AppVersieDTO {
  app: AppNaam
  versie: string | null
  sha: string | null
  shaKort: string | null
  liveOp: string | null
  liveOpGeschat: boolean
}

export interface AppDashboardTellersDTO {
  /** Live gegaan in de huidige ISO-week (NL). */
  liveDezeWeek: number
  /** Gemerged maar nog niet live (GEBOUWD). */
  wachtendOpLivegang: number
  /** ONTWERP, IN_BOUW en GEBOUWD samen (de tab "Nog niet live"). */
  nietLive: number
  /** Open actiepunten van FO-514 (OPEN, BEZIG, GEBLOKKEERD), over het hele register. */
  openActies: number
  blokkerendeActies: number
  /** Open beslispunten zonder besluit, over het hele register. */
  openBeslispunten: number
  /** Wijzigingen met een geschatte livegang of plek. */
  geschat: number
  waarOnbekend: number
  nietGeparseerd: number
}

export type AppDraaiendeVersieStaat = 'GELIJK' | 'AFWIJKEND' | 'ONBEKEND'

export interface AppDashboardKopDTO {
  versies: AppVersieDTO[]
  laatsteDeploy: AppDeployDTO | null
  tellers: AppDashboardTellersDTO
  /** De api zoals hij nu draait (APP_VERSION, BUILD_SHA), vergeleken met de laatste deploy. */
  draaiend: {
    versie: string | null
    sha: string | null
    staat: AppDraaiendeVersieStaat
    toelichting: string
  }
  /** De laatste keer dat de bronnen zijn ingelezen (sync, backfill of deploy). */
  laatsteSync: { op: string; soort: string } | null
  /** De modules die voorkomen (voor het filter). */
  modules: string[]
  magBeheren: boolean
}

export interface AppBronnenResultaatDTO {
  deploys: {
    nieuw: number
    bestaand: number
    herdeploys: number
    /** Sync: bump-commits zonder geslaagde run (mislukt, nog bezig of onbekend), niet vastgelegd. */
    zonderBewijs: number
  }
  wijzigingen: {
    nieuw: number
    bijgewerkt: number
    ongewijzigd: number
    nietMeerInBron: number
    live: number
    gebouwd: number
    inBouw: number
    ontwerp: number
  }
  secties: { totaal: number; nietGeparseerd: number; zonderFo: number; zonderWaar: number }
  specs: { totaal: number; metFo: number; foUitNummer: number; hernummerd: number }
  schattingen: { liveOpGeschat: number; waarGeschat: number; waarOnbekend: number }
  meldingen: { deployLive: number; nietGeregistreerd: number }
  /** Rijen waarbij het vastleggen technisch mislukte (de rest ging door; de volgende ronde probeert opnieuw). */
  fouten: Array<{ sleutel: string; fout: string }>
}

export interface AppDeployRegistratieResultaatDTO {
  deploy: AppDeployDTO
  /** false = deze registratie bestond al (idempotent, er is niets veranderd). */
  nieuw: boolean
  /** Gezet bij een herdeploy van een sha die al eerder live ging. */
  herdeployVanId: string | null
  /** Wijzigingen die met deze deploy live gingen. */
  wijzigingenLive: number
  bronnen: AppBronnenResultaatDTO | null
  /** Waarom de meegestuurde bronnen niet zijn verwerkt (nog geen terugwerkende import); anders null. */
  bronnenOvergeslagen: string | null
}
